/**
 * useLivePreview — debounce-driven preview that refetches whenever the
 * editable draft changes. The backend's `/preview` endpoint accepts a
 * partial config in the body, so the user never has to hit Save to see
 * the impact of an exclusion or formula edit.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FlowOptimizerPreviewResult } from '@trafficpulse/shared';

import {
  previewOptimizer,
  type FlowOptimizerProvider,
  type PreviewOptimizerDraft,
} from '../services/flow-optimizer-api';

export interface UseLivePreviewParams {
  campaignId: string;
  provider: FlowOptimizerProvider;
  draft: PreviewOptimizerDraft | null;
  debounceMs?: number;
  enabled?: boolean;
}

export interface UseLivePreviewResult {
  data: FlowOptimizerPreviewResult | null;
  isFetching: boolean;
  isStale: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useLivePreview(params: UseLivePreviewParams): UseLivePreviewResult {
  const { campaignId, provider, draft, debounceMs = 500, enabled = true } = params;
  const [data, setData] = useState<FlowOptimizerPreviewResult | null>(null);
  const [isFetching, setFetching] = useState(false);
  const [isStale, setStale] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const tokenRef = useRef(0);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const draftKey = useMemo(() => serializeDraft(draft), [draft]);

  useEffect(() => {
    if (!enabled || !draftRef.current) {
      setStale(false);
      return;
    }
    setStale(true);
    const timer = setTimeout(() => {
      const myToken = ++tokenRef.current;
      setFetching(true);
      const snapshot = draftRef.current;
      if (!snapshot) {
        setFetching(false);
        return;
      }
      previewOptimizer(campaignId, provider, snapshot)
        .then((result) => {
          if (myToken !== tokenRef.current) return;
          setData(result);
          setError(null);
          setStale(false);
        })
        .catch((err: Error) => {
          if (myToken !== tokenRef.current) return;
          setError(err);
        })
        .finally(() => {
          if (myToken !== tokenRef.current) return;
          setFetching(false);
        });
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [draftKey, campaignId, provider, debounceMs, enabled]);

  // Read the draft from the ref so this callback stays referentially stable
  // across keystrokes — otherwise the live-preview context value churns every
  // render and forces the (100+ row) preview consumers to re-render needlessly.
  const refetch = useCallback(() => {
    const snapshot = draftRef.current;
    if (!snapshot) return;
    tokenRef.current++;
    setFetching(true);
    setStale(true);
    previewOptimizer(campaignId, provider, snapshot)
      .then((result) => {
        setData(result);
        setError(null);
        setStale(false);
      })
      .catch((err: Error) => setError(err))
      .finally(() => setFetching(false));
  }, [campaignId, provider]);

  // Stable object identity unless one of these fields actually changes, so a
  // formula/exclusion keystroke doesn't ripple a new context value downstream.
  return useMemo(
    () => ({ data, isFetching, isStale, error, refetch }),
    [data, isFetching, isStale, error, refetch],
  );
}

function serializeDraft(draft: PreviewOptimizerDraft | null): string {
  if (!draft) return '';
  try {
    return JSON.stringify(draft);
  } catch {
    return Math.random().toString(36);
  }
}
